import { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { useAuth } from '../context/AuthContext';
import { useData } from '../context/DataProvider';
import { fetchProgressBatch } from '../services/onboardingService';
import { WORKING_ASSIGNMENT_STATUSES, DEFAULTS, isActiveJobStatus } from '../constants/enums';
import { computeAllAlerts, filterDismissedAlerts, dismissAlert as dismissAlertService } from '../services/alertService';
import { projectJob } from '../utils/projections/projectJob';
import { fetchJuuniScoreData, computeAllJuuniScores, persistJuuniScores } from '../services/juuniScoreService';
import { buildQualityIndexes, getDerivedAssignmentQuality } from '../utils/projectRollups';
import { safeQuery } from '../utils/safeSupabase';

export function useAdminData() {
  const { user, role, effectiveRole, effectiveOrgId } = useAuth();
  const { orgId } = useData();
  const activeAdminOrgId = effectiveOrgId || orgId || null;
  const currentUserId = user?.id || null;
  // SPL users see only their assigned projects. Admin/God users see everything.
  const isScopedRole = effectiveRole === 'spl';

  // Core entity state
  const [annotators, setAnnotators] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [applications, setApplications] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [qualityLogs, setQualityLogs] = useState([]);
  const [onboardingData, setOnboardingData] = useState({});
  const [clients, setClients] = useState([]);
  const [juuniScores, setJuuniScores] = useState({});
  const [allOrgs, setAllOrgs] = useState([]);
  const [splUsers, setSplUsers] = useState([]); // SPL/admin users for boss view
  const [batchSummary, setBatchSummary] = useState({}); // { jobId: totalItemsCompleted }
  const [batchRows, setBatchRows] = useState([]); // raw batch rows for alert service (Bug 35 fix)
  // Phase 5.75 B-15: datasets load into the admin context so BossDashboard
  // and SPLDashboard can both read a single org-scoped snapshot (stalled-
  // dataset detection + needs-provenance-review amber lane on the
  // attention strip, + status-summary strip). Raw snake_case to match the
  // other tables; dashboardAttention helpers accept both shapes.
  const [datasets, setDatasets] = useState([]);
  const [loading, setLoading] = useState(true);

  // View scope toggle — SPL can switch between "my section" and "full project"
  const [viewScope, setViewScope] = useState('my_section'); // 'my_section' | 'full_project'

  // Alerts
  const [, setDismissedAlertKeys] = useState([]);

  // DB log (for activity feed)
  const [dbLog, setDbLog] = useState([]);

  const logDbOp = useCallback((operation, table, details = '') => {
    const entry = {
      id: Date.now() + Math.random(),
      time: new Date().toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' }),
      operation,
      table,
      details,
    };
    setDbLog(prev => [entry, ...prev].slice(0, DEFAULTS.ACTIVITY_LOG_MAX));
  }, []);

  const retryCountRef = useRef(0);
  const requestIdRef = useRef(0); // Tracks the latest request — stale responses are discarded
  const MAX_RETRIES = 3;
  const loadData = useCallback(async (showLoading = true) => {
    // Skip fetch until auth has settled (fresh login race condition)
    if (!activeAdminOrgId) {
      if (showLoading) setLoading(false);
      return;
    }
    if (showLoading) setLoading(true);
    const thisRequestId = ++requestIdRef.current; // Increment to invalidate any in-flight request
    try {
      // Phase 15 Tier-2 dogfood close 2026-04-30 — filter GDPR Art 17
      // soft-deleted individuals (deleted_at IS NOT NULL = anonymized + retired
      // for FK integrity per migration 258). Without this filter, "deleted user"
      // entries surface in workforce roster + project teams, breaking
      // customer-1 demo credibility.
      let annotatorsQuery = supabase.from('individual').select('*, previous_companies, deel_onboarding_status, insightful_onboarding_status').is('deleted_at', null).order('name');
      let jobsQuery = supabase.from('job').select('*').order('created_at', { ascending: false });
      let appsQuery = supabase.from('application').select('*, job:job_id(title, domain, base_rate), applicant:applicant_id(name, primary_domain, badge, email, total_jobs_completed, resume_url, previous_companies, deel_onboarding_status, insightful_onboarding_status)').order('applied_at', { ascending: false });
      let clientsQuery = supabase.from('client').select('*').order('name');
      // Phase 23 deep-dive #12 (2026-05-01) — drop the
      // `.in('status', WORKING_ASSIGNMENT_STATUSES)` filter.
      // Pre-fix the loader filtered to Active+Onboarding only.
      // That under-counted cost on the SPL Dashboard (Projected
      // Margin showed 68% when reality was ~65% — same dive #1
      // class: Aisha's $4,310 of completed Highway Lane work
      // never made it into computeOrgFinancials, which then
      // extrapolated a too-low actual cost forward via the
      // ≥20%-complete branch). Downstream filters (line 469,
      // 523, SPLDashboard line 85, etc.) explicitly re-filter to
      // WORKING_ASSIGNMENT_STATUSES for staffing/active-counts,
      // so loading more statuses is a strict superset that fixes
      // cost/projection without changing anything else.
      let assignmentsQuery = supabase.from('assignment').select('*, individual:individual_id(name, primary_domain, email), job:job_id(title, domain, base_rate, client_display_name, uses_pipelines)').order('assigned_at', { ascending: false });
      let qualityLogsQuery = supabase.from('quality_log').select('job_id, individual_id, accuracy_percent').not('accuracy_percent', 'is', null);

      if (activeAdminOrgId) {
        annotatorsQuery = annotatorsQuery.eq('org_id', activeAdminOrgId);
        jobsQuery = jobsQuery.eq('org_id', activeAdminOrgId);
        appsQuery = appsQuery.eq('org_id', activeAdminOrgId);
        clientsQuery = clientsQuery.eq('org_id', activeAdminOrgId);
        assignmentsQuery = assignmentsQuery.eq('org_id', activeAdminOrgId);
        qualityLogsQuery = qualityLogsQuery.eq('org_id', activeAdminOrgId);
      }

      // Batch summary: lightweight query for items_completed per job (for schedule gauge)
      const batchQuery = supabase.from('annotation_batch')
        .select('job_id, items_completed')
        .eq('org_id', activeAdminOrgId)
        .eq('is_overlap', false);

      // Phase 5.75 B-15: lightweight datasets query — only the columns the
      // dashboard needs (status, provenance triad, status_changed_at, job_id).
      // RLS already scopes to this org; we also .eq org_id for belt-and-braces
      // consistency with the other adminData queries.
      const datasetsQuery = supabase.from('dataset')
        .select('id, name, status, license_type, pii_status, consent_status, status_changed_at, job_id')
        .eq('org_id', activeAdminOrgId);

      const [annotatorsRes, jobsRes, appsRes, clientsRes, assignmentsRes, qualityLogsRes, batchRes, datasetsRes] = await Promise.all([
        safeQuery(annotatorsQuery, 'adminData-annotators'),
        safeQuery(jobsQuery, 'adminData-jobs'),
        safeQuery(appsQuery, 'adminData-applications'),
        safeQuery(clientsQuery, 'adminData-clients'),
        safeQuery(assignmentsQuery, 'adminData-assignments'),
        safeQuery(qualityLogsQuery, 'adminData-qualityLogs'),
        safeQuery(batchQuery, 'adminData-batchSummary'),
        safeQuery(datasetsQuery, 'adminData-datasets'),
      ]);

      // Discard stale response — a newer request has started while this one was in-flight
      if (thisRequestId !== requestIdRef.current) {
        console.warn(`[adminData] Discarding stale response (request ${thisRequestId}, current ${requestIdRef.current})`);
        return;
      }

      // Check for critical fetch failures (jobs + assignments = core data)
      const criticalErrors = [jobsRes, assignmentsRes].filter(r => r.error);
      if (criticalErrors.length > 0) {
        if (retryCountRef.current < MAX_RETRIES) {
          retryCountRef.current++;
          const delay = Math.min(3000 * retryCountRef.current, 15000);
          console.warn(`[adminData] Critical fetch failed, retry ${retryCountRef.current}/${MAX_RETRIES} in ${delay}ms`);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('juuni:error', {
              detail: { message: `Failed to load data. Retrying (${retryCountRef.current}/${MAX_RETRIES})...` },
            }));
          }
          setTimeout(() => loadData(false), delay);
          return;
        } else {
          console.error('[adminData] All retries exhausted');
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('juuni:error', {
              detail: { message: 'Dashboard unavailable. Please refresh or check your connection.' },
            }));
          }
          setLoading(false);
          return;
        }
      }
      // Reset retry counter on success
      retryCountRef.current = 0;

      // Enrich annotators with derived tags (primary_domain + assignment domains + persisted tags)
      // Uses lookup map for O(n+m) instead of O(n×m) per-annotator filtering
      if (annotatorsRes.data) {
        const jobMap = new Map((jobsRes.data || []).map(j => [j.id, j]));
        const assignsByAnnotator = new Map();
        for (const as of (assignmentsRes.data || [])) {
          if (!assignsByAnnotator.has(as.individual_id)) assignsByAnnotator.set(as.individual_id, []);
          assignsByAnnotator.get(as.individual_id).push(as);
        }
        const enriched = annotatorsRes.data.map(a => {
          const persisted = a.tags || [];
          const domainTag = a.primary_domain ? [a.primary_domain] : [];
          const myAssigns = assignsByAnnotator.get(a.id) || [];
          const assignJobTags = myAssigns
            .map(as => jobMap.get(as.job_id))
            .filter(Boolean)
            .flatMap(j => [j.domain, ...(j.tags || [])].filter(Boolean));
          const allTags = [...new Set([...persisted, ...domainTag, ...assignJobTags])];
          return { ...a, tags: allTags };
        });
        setAnnotators(enriched);
      }
      if (jobsRes.data) setJobs(jobsRes.data);
      if (appsRes.data) setApplications(appsRes.data);
      if (clientsRes.data) setClients(clientsRes.data);
      if (qualityLogsRes.data) setQualityLogs(qualityLogsRes.data);
      // Build per-job batch summary: { jobId: totalItemsCompleted } + keep raw rows for alert service
      if (batchRes.data) {
        const summary = {};
        for (const b of batchRes.data) {
          if (b.job_id) summary[b.job_id] = (summary[b.job_id] || 0) + (b.items_completed || 0);
        }
        setBatchSummary(summary);
        setBatchRows(batchRes.data);
      }
      // Phase 5.75 B-15: hydrate org-wide datasets snapshot for dashboards.
      if (datasetsRes.data) setDatasets(datasetsRes.data);

      if (assignmentsRes.data) {
        setAssignments(assignmentsRes.data);
        if (assignmentsRes.data.length > 0) {
          try {
            const assignmentIds = assignmentsRes.data.map(a => a.id);
            const progressMap = await fetchProgressBatch(assignmentIds);
            const obMap = {};
            for (const [assignmentId, { rows, source }] of Object.entries(progressMap)) {
              const flatObj = { _source: source, _rows: rows };
              for (const row of rows) {
                const legacyKey = row._legacy_key || row.step_id;
                flatObj[legacyKey] = row.status;
              }
              if (rows.length > 0) {
                flatObj.id = rows[0].id;
                flatObj.individual_id = rows[0].individual_id;
                flatObj.job_id = rows[0].job_id;
                flatObj.org_id = rows[0].org_id;
                flatObj.assignment_id = assignmentId;
              }
              obMap[assignmentId] = flatObj;
            }
            setOnboardingData(obMap);
          } catch (err) {
            console.error('[adminData] fetchProgressBatch failed:', err.message);
            setOnboardingData({});
          }
        }
      }

      // Fetch SPL/admin users for boss view (non-blocking)
      // Phase 15 Tier-2 dogfood close 2026-04-30 — filter soft-deleted
      // individuals (same rationale as the annotators query above).
      if (!isScopedRole && activeAdminOrgId) {
        safeQuery(supabase.from('individual').select('id, name, email, role, primary_domain, status')
          .in('role', ['spl', 'admin'])
          .eq('org_id', activeAdminOrgId)
          .is('deleted_at', null), 'adminData-splUsers')
          .then(({ data }) => { if (data) setSplUsers(data); });
      }

      // Compute Juuni Scores (non-blocking)
      if (activeAdminOrgId && annotatorsRes.data?.length > 0) {
        fetchJuuniScoreData(activeAdminOrgId).then(scoreData => {
          const computedScores = computeAllJuuniScores(annotatorsRes.data, scoreData);
          setJuuniScores(computedScores);
          persistJuuniScores(annotatorsRes.data, computedScores).catch(e => console.warn('[adminData] Score persistence failed:', e.message));
        }).catch(e => console.warn('[adminData] Juuni Score computation failed:', e.message));
      }
    } catch (err) {
      console.error('Failed to load admin data:', err);
    } finally {
      setLoading(false);
    }
    // Phase 11 slot 12 (§8.5 row 13): `isScopedRole` is read at line 218
    // (boss-view splUsers fetch). Without it in the dep array, a role
    // transition mid-session (god → admin → spl via impersonation, or
    // SPL → admin via promotion without page reload) would leave
    // `loadData` carrying a stale `isScopedRole` closure — splUsers
    // would never load for a newly-promoted admin until full reload.
    // Adding `isScopedRole` keys the callback on the role-derived
    // primitive too. The 30s auto-refresh useEffect re-arms when
    // `loadData` changes, which is the correct behavior on role flips.
  }, [activeAdminOrgId, isScopedRole]);

  // Initial load + silent auto-refresh (interval is 30s — see DEFAULTS.AUTO_REFRESH_INTERVAL).
  //
  // Phase 11 slot 11 (2026-04-25) — re-render storm fix (cross-phase sweep
  // 13.1, audit Appendix B). Two changes:
  //
  //   1. **Pause auto-refresh when tab is hidden.** Admin tabs often stay
  //      open in the background while the user works elsewhere. Without
  //      pausing, the 30s tick fires through invisible tabs — burning
  //      bandwidth + DB query budget for nothing. Page Visibility API
  //      resumes immediately on visibilitychange so the user sees fresh
  //      data within the next tick after re-focusing the tab.
  //
  //   2. **Trigger one fresh fetch on visibility-restore.** When a user
  //      comes back to the tab after several minutes, we want fresh data
  //      immediately — not "wait up to 30s for the next tick." That's
  //      what `loadData(false)` does at the top of the visible branch.
  //
  // The audit appendix initially diagnosed this as "useEffect dep array
  // thrashing on AuthContext re-renders." Verified that's NOT the case
  // — the deps array is `[loadData]` and `loadData` is `useCallback` keyed
  // on `[activeAdminOrgId]` (a primitive string). Object.is compares
  // primitives by value; the callback is stable across token-refresh
  // re-renders that don't change the org_id. The 248 fires in the audit
  // session = 30s × 248 ≈ 124 min ≈ 2-hour admin session = the literal
  // setInterval count, not a render storm.
  useEffect(() => {
    if (typeof document === 'undefined') {
      // SSR / test environment without document — fall back to interval-only.
      loadData(true);
      const fallback = setInterval(() => loadData(false), DEFAULTS.AUTO_REFRESH_INTERVAL);
      return () => clearInterval(fallback);
    }

    let interval = null;
    let mounted = true;

    function startInterval() {
      if (interval) return;
      interval = setInterval(() => {
        if (mounted && !document.hidden) loadData(false);
      }, DEFAULTS.AUTO_REFRESH_INTERVAL);
    }
    function stopInterval() {
      if (interval) { clearInterval(interval); interval = null; }
    }
    function onVisibilityChange() {
      if (document.hidden) {
        stopInterval();
      } else {
        // Tab is visible again — fetch fresh data immediately, then resume polling.
        if (mounted) loadData(false);
        startInterval();
      }
    }

    // Initial fetch (with loading spinner) + interval start
    loadData(true);
    if (!document.hidden) startInterval();
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      mounted = false;
      stopInterval();
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [loadData]);

  // Load all orgs for god-role users
  useEffect(() => {
    if (role === 'god') {
      safeQuery(supabase.from('organization').select('*').order('name'), 'adminData-allOrgs').then(({ data }) => {
        if (data) setAllOrgs(data);
      });
    } else {
      setAllOrgs([]);
    }
  }, [role]);

  // Quality helpers
  const qualityIndexes = useMemo(() => buildQualityIndexes(qualityLogs), [qualityLogs]);

  function getAssignmentQuality(assignment) {
    return getDerivedAssignmentQuality({
      jobId: assignment?.job_id || assignment?.job?.id,
      individualId: assignment?.individual_id || assignment?.individual?.id,
      fallbackQuality: assignment?.quality_score,
      qualityIndexes,
    });
  }

  function getIndividualQuality(individualId, fallbackQuality = null) {
    return getDerivedAssignmentQuality({ individualId, fallbackQuality, qualityIndexes });
  }

  // ─── SPL Scoping ───
  // Determine if scoping is active (SPL in "my section" mode)
  const isScopeActive = isScopedRole && viewScope === 'my_section';

  // Helper: check if a job belongs to this SPL
  const isMyJob = useCallback((j) => {
    return j.project_manager_id === currentUserId || (j.pm_ids || []).includes(currentUserId);
  }, [currentUserId]);

  // Helper: check if an assignment belongs to this SPL
  const isMyAssignment = useCallback((a, myJobIdSet) => {
    return a.managed_by === currentUserId || (!a.managed_by && myJobIdSet.has(a.job_id));
  }, [currentUserId]);

  // myJobs: scoped or all (with _isMine flag in full project mode)
  const myJobs = useMemo(() => {
    if (!isScopedRole) return jobs;
    if (isScopeActive) return jobs.filter(isMyJob);
    // Full project mode: return all jobs, tag which ones are mine
    return jobs.map(j => ({ ...j, _isMine: isMyJob(j) }));
  }, [jobs, isScopedRole, isScopeActive, isMyJob]);


  // Set of just MY job IDs (for highlighting in full project mode)
  const ownJobIds = useMemo(() => new Set(
    jobs.filter(isMyJob).map(j => j.id)
  ), [jobs, isMyJob]);

  // myAssignments: scoped or all (with _isMine flag)
  const myAssignments = useMemo(() => {
    if (!isScopedRole) return assignments;
    if (isScopeActive) return assignments.filter(a => isMyAssignment(a, ownJobIds));
    return assignments.map(a => ({ ...a, _isMine: isMyAssignment(a, ownJobIds) }));
  }, [assignments, isScopedRole, isScopeActive, isMyAssignment, ownJobIds]);

  // myApplications: scoped or all (with _isMine flag)
  const myApplications = useMemo(() => {
    if (!isScopedRole) return applications;
    if (isScopeActive) return applications.filter(a => ownJobIds.has(a.job_id));
    return applications.map(a => ({ ...a, _isMine: ownJobIds.has(a.job_id) }));
  }, [applications, isScopedRole, isScopeActive, ownJobIds]);

  // Phase 5.5 Step 6 — projection config for alert-path projectJob calls.
  const PROJECTION_CONFIG_ALERTS = useMemo(() => ({
    PROJECTION_BAND_CONFIDENCE: DEFAULTS.PROJECTION_BAND_CONFIDENCE,
    PROJECTION_BAND_Z: DEFAULTS.PROJECTION_BAND_Z,
    PROJECTION_BAND_P_LOW: DEFAULTS.PROJECTION_BAND_P_LOW,
    PROJECTION_BAND_P_HIGH: DEFAULTS.PROJECTION_BAND_P_HIGH,
    PROJECTION_MIN_ANNOTATORS_FOR_HIGH_CONF: DEFAULTS.PROJECTION_MIN_ANNOTATORS_FOR_HIGH_CONF,
    PROJECTION_MIN_WEEKS_FOR_HIGH_CONF: DEFAULTS.PROJECTION_MIN_WEEKS_FOR_HIGH_CONF,
    PROJECTION_MIN_ANNOTATORS_FOR_MEDIUM_CONF: DEFAULTS.PROJECTION_MIN_ANNOTATORS_FOR_MEDIUM_CONF,
    PROJECTION_MIN_WEEKS_FOR_MEDIUM_CONF: DEFAULTS.PROJECTION_MIN_WEEKS_FOR_MEDIUM_CONF,
    PROJECTION_MIN_SAMPLES_FOR_PERCENTILE: DEFAULTS.PROJECTION_MIN_SAMPLES_FOR_PERCENTILE,
    PROJECTION_QUALITY_FACTOR_MAX: DEFAULTS.PROJECTION_QUALITY_FACTOR_MAX,
  }), []);

  // R5 MED-2/MED-3 + R6 BLOCKER-1 audit fix: in-memory tier-transition
  // history per jobId, used for projection-alert hysteresis. The R5
  // implementation had a critical ordering bug — the ref was mutated
  // INSIDE the `allAlerts` useMemo BEFORE `computeAllAlerts` ran, so
  // `hasPersisted` saw the just-prepended current tier in the "prior"
  // window and trivially matched it. Hysteresis fired on the first
  // observation, which defeats the whole point.
  //
  // R6 BLOCKER-1 fix: ref mutation moved into a useEffect that runs
  // AFTER commit. On render N, `allAlerts` reads the PRIOR history
  // (set by render N-1's effect) — so hasPersisted correctly checks
  // whether the current tier has persisted across actually-prior
  // observations, not just against itself.
  //
  // R6 MED-2 fix: useMemo is now pure — no ref mutation during render.
  // Safe under React 18+ StrictMode double-invocation and concurrent
  // rendering.
  //
  // R6 MED-3 fix: stale jobIds (archived/removed jobs) are deleted
  // from the ref in the same effect to prevent unbounded growth.
  //
  // R7 LOW-1 note: the useMemo below depends on batchRows in its dep
  // array; the useEffect does NOT. This is intentional — tier-history
  // state depends only on projection INPUTS (which read batchSummary,
  // not raw batchRows). A future refactor that starts reading
  // batchRows inside projectJob MUST add batchRows to the effect deps
  // too. Do not remove this comment without checking projectJob's
  // reads.
  const projectionHistoriesRef = useRef({});
  const SNAPSHOT_WINDOW = 5;

  // R7 LOW-3 audit fix: capture `now` ONCE per hook execution so the
  // useMemo and the useEffect below both see the same wall-clock
  // instant. Without this, the memo's projectJob and the effect's
  // projectJob each default to `new Date()` at their respective
  // execution times — at a tier boundary (wall-clock at the exact
  // deadline moment), the two could land on different tiers and the
  // recorded history would disagree with the alerted tier.
  //
  // R12 LOW-2 audit fix: previously used `useRef` + inline mutation
  // (`nowRef.current = new Date()`) during render — that violates React's
  // "no ref mutation during render" contract. Under StrictMode's double-
  // render (dev-only), the discarded render's timestamp could clobber the
  // one the committed render's memo + effect needed to agree on. A
  // dep-synced `useMemo` is the canonical pattern: stable across renders
  // until deps change, no render-time side effects.
  const sharedNow = useMemo(
    () => new Date(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [myJobs, assignments, qualityLogs, batchRows, batchSummary, PROJECTION_CONFIG_ALERTS],
  );

  const allAlerts = useMemo(() => {
    const activeJobs = myJobs.filter(j => isActiveJobStatus(j.status));
    const result = [];
    for (const job of activeJobs) {
      // R6 HIGH-1 audit fix: filter to WORKING_ASSIGNMENT_STATUSES
      // (ACTIVE + ONBOARDING). Prior filter was ACTIVE-only, which
      // excluded ONBOARDING assignments from the alert-path projectJob
      // while every other dashboard caller included them — making the
      // admin-wide alert feed's `dollarsSpent` differ from the per-
      // dashboard display. Same job ⇒ same numbers is the invariant.
      const jobAssigns = assignments.filter(a => a.job_id === job.id && WORKING_ASSIGNMENT_STATUSES.includes(a.status));
      const jobBatches = batchRows.filter(b => b.job_id === job.id);
      const projection = projectJob({
        job,
        assignments: jobAssigns,
        qualityLogs: qualityLogs.filter(q => q.job_id === job.id),
        batchSummary: batchSummary || {},
        config: PROJECTION_CONFIG_ALERTS,
        now: sharedNow,
      });

      // Hysteresis: pass the PRIOR history (from previous render's
      // effect). Current tier is NOT yet in this history — that's
      // what makes `hasPersisted` check against genuine prior
      // observations, not against itself.
      const priorHistory = projectionHistoriesRef.current[job.id] || { deadline: [], cost: [] };

      const jobAlerts = computeAllAlerts({
        assignments: jobAssigns,
        batches: jobBatches,
        job,
        qualityLogs: qualityLogs.filter(q => q.job_id === job.id),
        maxHoursPerWeek: job.hours_per_week_max || DEFAULTS.MAX_HOURS_PER_WEEK,
        projection,
        projectionSnapshotHistory: priorHistory,
        minSnapshotsForHysteresis: 2,  // require 1 prior confirming snapshot
        // R13 MED-2 audit fix: pin the alert-compute wall-clock to the same
        // `sharedNow` the projection was computed with. Previously the alert
        // path read a fresh Date.now() → 1-day drift at date boundaries.
        now: sharedNow,
      });
      for (const alert of jobAlerts) {
        if (!alert.jobId) alert.jobId = job.id;
      }
      result.push(...jobAlerts);
    }
    return result;
  }, [myJobs, assignments, qualityLogs, batchRows, batchSummary, PROJECTION_CONFIG_ALERTS, sharedNow]);

  // R6 BLOCKER-1 + MED-2 + MED-3 audit fix: update the tier-transition
  // history AFTER render commits. This makes useMemo pure and ensures
  // the next render sees the CURRENT tier as a prior observation.
  // Also prunes ref entries for jobs that are no longer active
  // (archived / removed / out-of-scope) so the ref doesn't grow
  // unboundedly across a long admin session.
  useEffect(() => {
    const activeJobs = myJobs.filter(j => isActiveJobStatus(j.status));
    const activeJobIdSet = new Set(activeJobs.map(j => j.id));
    // R7 LOW-3 + R12 LOW-2 audit fix: `sharedNow` is a useMemo above — same
    // wall-clock instant seen by the memo and this effect, no render-time
    // ref mutation.
    const nowMs = sharedNow.getTime();

    for (const job of activeJobs) {
      const jobAssigns = assignments.filter(a => a.job_id === job.id && WORKING_ASSIGNMENT_STATUSES.includes(a.status));
      const projection = projectJob({
        job,
        assignments: jobAssigns,
        qualityLogs: qualityLogs.filter(q => q.job_id === job.id),
        batchSummary: batchSummary || {},
        config: PROJECTION_CONFIG_ALERTS,
        now: sharedNow,
      });
      const dhTier = projection?.deadlineHit?.tier || null;
      const coTier = projection?.costOverrun?.tier || null;
      const prev = projectionHistoriesRef.current[job.id] || { deadline: [], cost: [] };
      const lastDh = prev.deadline[0]?.tier ?? null;
      const lastCo = prev.cost[0]?.tier ?? null;
      projectionHistoriesRef.current[job.id] = {
        deadline: dhTier !== lastDh
          ? [{ tier: dhTier, computedAt: nowMs }, ...prev.deadline].slice(0, SNAPSHOT_WINDOW)
          : prev.deadline,
        cost: coTier !== lastCo
          ? [{ tier: coTier, computedAt: nowMs }, ...prev.cost].slice(0, SNAPSHOT_WINDOW)
          : prev.cost,
      };
    }

    // Prune stale entries (jobs that are no longer active).
    for (const id of Object.keys(projectionHistoriesRef.current)) {
      if (!activeJobIdSet.has(id)) {
        delete projectionHistoriesRef.current[id];
      }
    }
  }, [myJobs, assignments, qualityLogs, batchSummary, PROJECTION_CONFIG_ALERTS, sharedNow]);
  const visibleAlerts = filterDismissedAlerts(allAlerts);

  function dismissAlert(key) {
    setDismissedAlertKeys(prev => [...prev, key]);
    dismissAlertService(key);
  }

  function restoreAllAlerts() {
    setDismissedAlertKeys([]);
    // Clear all dismissed alert keys from localStorage (alertService stores per-key)
    try {
      const keys = Object.keys(localStorage).filter(k => k.startsWith('alert_dismissed_'));
      keys.forEach(k => localStorage.removeItem(k));
    } catch { /* localStorage blocked (safari private, quota, etc.) — non-critical */ }
  }

  return {
    // Core data (unscoped — full org data for search/talent pool)
    annotators, jobs, applications, assignments, qualityLogs,
    onboardingData, clients, juuniScores, allOrgs, batchSummary, datasets,
    loading, loadData, logDbOp, dbLog,
    // SPL-scoped data (filtered by project_manager_id for SPL role)
    myJobs, myAssignments, myApplications, isScopedRole, splUsers,
    // Scope toggle
    viewScope, setViewScope, isScopeActive, ownJobIds,
    // Quality
    qualityIndexes, getAssignmentQuality, getIndividualQuality,
    // Alerts (already scoped to myJobs)
    alerts: allAlerts, visibleAlerts, dismissAlert, restoreAllAlerts,
    dismissedCount: allAlerts.length - visibleAlerts.length,
    // Org context
    activeAdminOrgId,
  };
}
